import React, { useEffect, useMemo, useState } from 'react';
import * as XLSX from 'xlsx';
import ListGrid from '../../../components/ListGrid';
import EmptyScreen from '../../../components/EmptyScreen';
import { Figure, Figures } from '../../../components/Figures';
import { cellInputStyle, CELL_CLASS } from './movementUi';
import {
  readFileAsText, parseReport, lookupItem, buildReorderCsv, downloadCsv, todayStamp,
} from './movementUtils';

/*
  Movement, Weekly Reorder. Built to Group G's approved drawing on 8 September
  2026.

  **A different job from reading pages, on the same screen as reading pages.**
  Nothing handwritten is involved: this takes the week's movement report out of
  the system, adds each item up across every time it appears, and gives one line
  per item to reorder.

  -- What changed from what was here, both named in the group's README --------

  - **The three warnings that were stacked into one amber block became one line
    each, on the row they are about.** A warning that names seven item numbers
    in a paragraph is a warning nobody reads, and the block sat above a table of
    two hundred rows with no way to tell which was which.
  - **A row whose quantity was positive says "A return is in this"** rather than
    only being tinted. A positive quantity in a movement report is a return, and
    folding a return into a reorder is a real decision somebody should make on
    purpose rather than a shade of yellow.

  Two more, which the group's shape carries rather than its README:

  - **The four figures sit on hairlines**, rule 5, shared with Review and Run
    History rather than written a third time.
  - **The two downloads became one primary act and one quiet one**, the same
    split as Review: the import file is the job, and the spreadsheet is a copy
    for your own records.

  -- The one thing the drawing does not carry --------------------------------

  **Which lines were skipped, and why.** The drawing has the count, in the
  figures and under the file's name, and no list of them. Dropping the detail
  altogether would lose something real, so it goes into the copy for your
  records, on a sheet of its own, which is where a record belongs. The screen is
  the drawing.
*/
export default function ReorderTab({
  rows, setRows, skipped, setSkipped, meta, setMeta, products, productIndex, toast, setBar,
}) {
  const [search, setSearch] = useState('');
  const [only, setOnly] = useState('all');   // all | returns | unknown

  async function onReportFile(e) {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const text = await readFileAsText(file);
      const { rows: parsed, skipped: skips, dataRowCount } = parseReport(text);
      setRows(parsed);
      setSkipped(skips);
      setMeta({ fileName: file.name, dataRowCount });
      toast.success(`${parsed.length} item${parsed.length === 1 ? '' : 's'} from ${dataRowCount} report rows.`);
    } catch (err) {
      toast.error('Nothing was loaded: ' + err.message);
    }
    e.target.value = '';
  }

  const totalUnits = useMemo(
    () => rows.reduce((n, r) => n + (parseInt(r.qty, 10) || 0), 0),
    [rows],
  );

  /* Each row's two flags worked out once. They are read by the row, by the
     filter and by the two counts the filter is offered on. */
  const flagged = useMemo(() => {
    const m = new Map();
    for (const r of rows) {
      m.set(r, {
        hasReturn: !!r.hadPositive,
        unknown: products.length ? !lookupItem(r.itemNumber, products, productIndex) : false,
      });
    }
    return m;
  }, [rows, products, productIndex]);

  const returns = rows.filter(r => flagged.get(r)?.hasReturn).length;
  const unknown = rows.filter(r => flagged.get(r)?.unknown).length;

  const visible = useMemo(() => {
    const q = search.trim().toUpperCase();
    return rows.filter(r => {
      const f = flagged.get(r) || {};
      if (only === 'returns' && !f.hasReturn) return false;
      if (only === 'unknown' && !f.unknown) return false;
      if (!q) return true;
      return (r.itemNumber || '').toUpperCase().includes(q)
        || (r.description || '').toUpperCase().includes(q);
    });
  }, [rows, search, only, flagged]);

  function updateQty(row, value) {
    setRows(prev => prev.map(r => (r === row ? { ...r, qty: value } : r)));
  }

  /*
    **A copy for your records.** Every item, with what each one was made of, and
    the skipped lines on a sheet of their own so nothing the report threw away
    is lost with it.
  */
  function saveACopy() {
    if (!rows.length) { toast.error('There is nothing to save yet.'); return; }
    const data = [['Item number', 'Description', 'Times', 'Each time', 'Reorder', 'In the catalog']];
    for (const r of rows) {
      const f = flagged.get(r) || {};
      data.push([r.itemNumber, r.description, r.values.length, r.values.join(', '), r.qty,
                 products.length ? (f.unknown ? 'No' : 'Yes') : 'No catalog loaded']);
    }
    const ws = XLSX.utils.aoa_to_sheet(data);
    ws['!cols'] = [{ wch: 22 }, { wch: 48 }, { wch: 10 }, { wch: 24 }, { wch: 12 }, { wch: 18 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Reorder');

    /* The detail the screen does not carry. A count of three on its own does
       not tell anybody which three, and this is where that belongs. */
    if (skipped.length) {
      const s = [['Line in the file', 'Item number', 'Why it was skipped']];
      for (const x of skipped) s.push([x.row, x.item || '', x.reason]);
      const ws2 = XLSX.utils.aoa_to_sheet(s);
      ws2['!cols'] = [{ wch: 16 }, { wch: 22 }, { wch: 40 }];
      XLSX.utils.book_append_sheet(wb, ws2, 'Skipped lines');
    }

    XLSX.writeFile(wb, `OnHand_Reorder_${todayStamp()}.xlsx`);
    toast.success(rows.length + (rows.length === 1 ? ' item saved.' : ' items saved.'));
  }

  function makeTheFile() {
    if (!rows.length) { toast.error('There is nothing to put in a file.'); return; }
    downloadCsv(buildReorderCsv(rows), `OnHand_Reorder_Import_${todayStamp()}.csv`);
    toast.success(`${rows.length} item${rows.length === 1 ? '' : 's'} exported.`);
  }

  const upload = (label, primary) => (
    <label className="mv-upload">
      <input type="file" accept=".csv,text/csv" style={{ display: 'none' }} onChange={onReportFile} />
      <span className={'btn ' + (primary ? 'btn-primary' : 'btn-quiet')} role="button">{label}</span>
    </label>
  );

  // The bar's own row, handed up so there is still one bar (rule 26).
  useEffect(() => {
    if (!setBar) return;
    setBar({
      meta: rows.length ? rows.length + (rows.length === 1 ? ' item' : ' items') : null,
      search: rows.length
        ? { value: search, onChange: setSearch, placeholder: 'Search an item' }
        : null,
      // The filter goes behind the Filters button, the two acts stay out. See
      // the note on Review's bar: on a phone every action gets its own row.
      filterCount: only !== 'all' ? 1 : 0,
      drawer: rows.length && (returns > 0 || unknown > 0) ? (
        <>
          <span className="tb-lbl">Show</span>
          <select className={'tb-select' + (only !== 'all' ? ' set' : '')}
            value={only} onChange={e => setOnly(e.target.value)}>
            <option value="all">All items</option>
            {returns > 0 && <option value="returns">The {returns} with a return in</option>}
            {unknown > 0 && <option value="unknown">The {unknown} not in the catalog</option>}
          </select>
          <span className="tb-grow" />
          {only !== 'all' && (
            <button className="tb-btn tb-btn-danger" onClick={() => setOnly('all')}>Clear filters</button>
          )}
        </>
      ) : null,
      /* The copy is the quiet act, so it folds behind the "..." on a phone.
         Making the file is what you came for and never folds (rule 33). */
      quiet: rows.length ? (
        <button className="tb-btn tb-btn-ghost" onClick={saveACopy}>A copy for your records</button>
      ) : null,
      actions: rows.length ? (
        <button className="tb-btn tb-btn-primary" onClick={makeTheFile}>Make the file</button>
      ) : null,
    });
    // saveACopy and makeTheFile read `rows` and `skipped`, which the first
    // dependency and the counts already stand for.
  }, [rows, search, only, returns, unknown, setBar]); // eslint-disable-line

  /*
    **The empty version is the primary drawing here.** Nothing the movement tool
    does has ever been saved, so this screen has met every person who has opened
    it with nothing on it, every time. Group G drew it that way on purpose and
    the sentence explains what the screen is FOR rather than only what is
    missing: it is a different job from reading pages, and somebody landing on
    the sixth tab of a screen has every reason not to know that.
  */
  if (!rows.length && !skipped.length) {
    return (
      <EmptyScreen
        title="No movement report loaded"
        says={"A different job from reading pages: upload the week's movement report and OnHand "
            + 'adds each item up across every time it appears, so you get one line per item to reorder.'}
      >
        {upload('Upload the report', true)}
      </EmptyScreen>
    );
  }

  return (
    <div>
      {/* The file this came out of, and what it cost to read. The skipped count
          sits here as well as in the figures because this is the sentence about
          the file rather than about the items. */}
      <div className="card mv-file">
        <span style={{ minWidth: 0 }}>
          {/* Plain bold, as the drawing has it. `.mv-file-name` is the boxed,
              code-like treatment Run History gives the file a run MADE; this is
              the file a person just chose off their own machine. */}
          <span style={{ display: 'block', fontSize: 13.5, fontWeight: 800, color: 'var(--text-primary)', wordBreak: 'break-all' }}>
            {meta.fileName || 'A movement report'}
          </span>
          <span className="mv-file-label" style={{ display: 'block', marginTop: 3 }}>
            {(meta.dataRowCount || 0).toLocaleString()} rows read
            {skipped.length ? ' - ' + skipped.length + ' skipped' : ''}
          </span>
        </span>
        <span style={{ flex: 1 }} />
        {upload('Replace', false)}
      </div>

      <Figures>
        <Figure n={(meta.dataRowCount || 0).toLocaleString()} label="Rows in the report" />
        <Figure n={rows.length.toLocaleString()} label="Items to reorder" />
        <Figure n={totalUnits.toLocaleString()} label="Units" />
        <Figure n={skipped.length} label="Rows skipped"
          tone={skipped.length ? 'var(--critical-ink)' : undefined} />
      </Figures>

      <div className="rplist">
        {visible.length === 0 ? (
          <div style={{ padding: 28, textAlign: 'center', color: 'var(--text-secondary)', fontSize: 13 }}>
            {rows.length
              ? 'Nothing matches what you are looking for.'
              : 'Every row in that report was skipped, so there is nothing to reorder.'}
          </div>
        ) : (
          <ListGrid
            columns={[
              { label: 'Item number', width: 'minmax(0,0.9fr)' },
              { label: 'Description', width: 'minmax(0,1.6fr)' },
              { label: 'Times', width: '80px' },
              { label: 'Each time', width: 'minmax(0,0.9fr)' },
              { label: 'Reorder', width: '110px', align: 'right' },
              { key: 'flag', label: '', width: '190px' },
            ]}
            rows={visible}
            keyOf={r => r.itemNumber}
            /*
              **The bar and the mark have to agree, and a row can be both.**
              Found 8 September 2026 by photographing the loaded screen for the
              first time: `100002-030` had a red edge and an amber chip, because
              the edge answered "not in the catalog" and the chip answered "a
              return is in this" and nothing made them one decision. The edge
              takes the worse of the two and the row says both, so neither fact
              is hidden by the other.
            */
            rowClass={r => {
              const f = flagged.get(r) || {};
              return 'mv-line ' + (f.unknown ? 'bad' : f.hasReturn ? 'warn' : 'ok');
            }}
            cells={r => {
              const f = flagged.get(r) || {};
              return [
                <span className="mv-item">{r.itemNumber}</span>,
                r.description
                  ? <span className="rpdesc">{r.description}</span>
                  : <span className="rpdash">Not in the catalog</span>,
                <span className="rpvend">{r.values.length}</span>,
                <span className="rpvend">{r.values.join(', ')}</span>,
                <input className={CELL_CLASS} style={{ ...cellInputStyle, width: 68, textAlign: 'right' }}
                  value={r.qty} onChange={e => updateQty(r, e.target.value)} />,
                /*
                  **One line on the row it is about**, which is the whole of the
                  group's first change to this screen. Both are said when both
                  are true: the item number is the receiving system's problem
                  and the return is a decision somebody is making without
                  meaning to, and neither cancels the other.
                */
                <span style={{ display: 'inline-flex', gap: 6, flexWrap: 'wrap' }}>
                  {f.unknown && <span className="mv-outcome bad">Not in catalog</span>}
                  {f.hasReturn && <span className="mv-outcome warn">A return is in this</span>}
                </span>,
              ];
            }}
            stack={r => {
              const f = flagged.get(r) || {};
              return (
                <span>
                  <span style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span className="mv-item" style={{ display: 'block' }}>{r.itemNumber}</span>
                      <span style={{ display: 'block', fontSize: 13, marginTop: 2 }}>
                        {r.description || <span className="rpdash">Not in the catalog</span>}
                      </span>
                      <span className="rpvend" style={{ display: 'block', marginTop: 3 }}>
                        {r.values.length} time{r.values.length === 1 ? '' : 's'} - {r.values.join(', ')}
                      </span>
                    </span>
                    <input className={CELL_CLASS} style={{ ...cellInputStyle, width: 68, textAlign: 'right' }}
                      value={r.qty} onChange={e => updateQty(r, e.target.value)} />
                  </span>
                  {(f.hasReturn || f.unknown) && (
                    <span style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
                      {f.unknown && <span className="mv-outcome bad">Not in catalog</span>}
                      {f.hasReturn && <span className="mv-outcome warn">A return is in this</span>}
                    </span>
                  )}
                </span>
              );
            }}
          />
        )}
      </div>
    </div>
  );
}
